import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding, resolveLogoUrl } from "@/context/BrandingContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { LogOut, PlusCircle, Package, History, ShoppingCart, Ban, Wallet } from "lucide-react";
import FinanceiroPanel from "@/pages/FinanceiroPanel";

const STATUS_STYLE = {
  pendente: "bg-amber-100 text-amber-800 border-amber-200",
  atendido: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelado: "bg-slate-200 text-slate-600 border-slate-300",
};
const STATUS_LABEL = { pendente: "Pendente", atendido: "Atendido", cancelado: "Suspenso" };

export default function RequesterPortal() {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const nav = useNavigate();

  const [itens, setItens] = useState([]);
  const [pedidos, setPedidos] = useState([]);
  const [itemId, setItemId] = useState("");
  const [descricao, setDescricao] = useState("");
  const [quantidade, setQuantidade] = useState("");
  const [observacao, setObservacao] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [suspending, setSuspending] = useState(null);

  const load = async () => {
    try {
      const [i, p] = await Promise.all([api.get("/itens"), api.get("/pedidos/meus")]);
      setItens(i.data);
      setPedidos(p.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const doLogout = () => { logout(); nav("/"); };

  const submitPedido = async (e) => {
    e.preventDefault();
    if (!itemId) { toast.error("Selecione um item"); return; }
    if (!quantidade || Number(quantidade) <= 0) { toast.error("Informe a quantidade"); return; }
    setSubmitting(true);
    try {
      await api.post("/pedidos", {
        item_id: itemId,
        descricao: descricao.trim() || itens.find(i => i.id === itemId)?.nome || "",
        quantidade: Number(quantidade),
        observacao,
      });
      toast.success("Pedido registrado!");
      setDescricao(""); setQuantidade(""); setObservacao(""); setItemId("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const confirmSuspend = async () => {
    if (!suspending) return;
    try {
      await api.post(`/pedidos/${suspending.id}/suspender`);
      toast.success("Pedido suspenso");
      setPedidos(prev => prev.map(p => p.id === suspending.id ? { ...p, status: "cancelado" } : p));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSuspending(null); }
  };

  const logo = resolveLogoUrl(branding.logo_url);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logo ? (
              <img src={logo} alt="logo" className="h-9 w-9 rounded object-cover" />
            ) : (
              <div className="h-9 w-9 rounded bg-brand text-white flex items-center justify-center">
                <Package className="h-4 w-4" />
              </div>
            )}
            <div>
              <div className="font-heading font-bold text-slate-900">{branding.company_name}</div>
              <div className="text-xs text-slate-500">Solicitante: {user?.nome || user?.username}</div>
            </div>
          </div>
          <Button data-testid="req-logout-btn" variant="outline" size="sm" onClick={doLogout} className="rounded-full">
            <LogOut className="h-4 w-4 mr-2" /> Sair
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="novo" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="novo" data-testid="tab-novo" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <PlusCircle className="h-4 w-4 mr-2" /> Novo pedido
            </TabsTrigger>
            <TabsTrigger value="hist" data-testid="tab-historico" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <History className="h-4 w-4 mr-2" /> Meu histórico
            </TabsTrigger>
            <TabsTrigger value="fin" data-testid="tab-financeiro" className="data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              <Wallet className="h-4 w-4 mr-2" /> Meu financeiro
            </TabsTrigger>
          </TabsList>

          <TabsContent value="novo">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Nova solicitação</CardTitle>
                <CardDescription>Preencha as informações do pedido abaixo.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={submitPedido} className="grid md:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label>Solicitante</Label>
                    <Input value={user?.nome || user?.username || ""} disabled data-testid="req-solicitante-display" />
                  </div>
                  <div className="space-y-2">
                    <Label>Item</Label>
                    <Select value={itemId} onValueChange={setItemId}>
                      <SelectTrigger data-testid="req-item-select"><SelectValue placeholder="Escolha um item" /></SelectTrigger>
                      <SelectContent>
                        {itens.length === 0 && <div className="p-2 text-sm text-slate-500">Nenhum item cadastrado.</div>}
                        {itens.map(it => (<SelectItem key={it.id} value={it.id}>{it.nome} ({it.unidade})</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Informe o nome da escola, unidade ou local onde o toner foi solicitado.</Label>
                    <Input data-testid="req-descricao-input" value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex: Escola Municipal João da Silva - Sala 12" />
                  </div>
                  <div className="space-y-2">
                    <Label>Quantidade</Label>
                    <Input data-testid="req-quantidade-input" type="number" min="0" step="any" value={quantidade} onChange={(e) => setQuantidade(e.target.value)} placeholder="0" required />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Observação (opcional)</Label>
                    <Textarea data-testid="req-observacao-input" value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Prazo, urgência, local de entrega..." rows={3} />
                  </div>
                  <div className="md:col-span-2">
                    <Button type="submit" data-testid="req-submit-pedido" disabled={submitting} className="btn-brand rounded-full h-11 px-8">
                      <ShoppingCart className="h-4 w-4 mr-2" /> {submitting ? "Enviando..." : "Enviar pedido"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="hist">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Meus pedidos</CardTitle>
                <CardDescription>{pedidos.length} pedido(s) no histórico. Você pode suspender pedidos pendentes.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border border-slate-200 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead><TableHead>Item</TableHead>
                        <TableHead>Descrição</TableHead><TableHead className="text-right">Quantidade</TableHead>
                        <TableHead>Observação</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-32">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pedidos.length === 0 && (<TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Nenhum pedido ainda.</TableCell></TableRow>)}
                      {pedidos.map((p, idx) => {
                        const st = p.status || "pendente";
                        const canSuspend = st === "pendente";
                        return (
                          <TableRow key={p.id} className="row-fade" style={{ animationDelay: `${idx * 30}ms` }}>
                            <TableCell className="text-sm text-slate-600">{new Date(p.created_at).toLocaleString("pt-BR")}</TableCell>
                            <TableCell className="font-medium">{p.item_nome}</TableCell>
                            <TableCell className="text-sm text-slate-600">{p.descricao}</TableCell>
                            <TableCell className="text-right font-mono">{p.quantidade} {p.unidade}</TableCell>
                            <TableCell className="text-sm text-slate-500 max-w-[220px] whitespace-pre-wrap">{p.observacao || "—"}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={STATUS_STYLE[st]}>{STATUS_LABEL[st]}</Badge>
                            </TableCell>
                            <TableCell>
                              {canSuspend && (
                                <Button
                                  size="sm" variant="outline"
                                  onClick={() => setSuspending(p)}
                                  data-testid={`suspend-btn-${p.id}`}
                                  className="rounded-full text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
                                >
                                  <Ban className="h-3 w-3 mr-1" /> Suspender
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="fin">
            <FinanceiroPanel />
          </TabsContent>
        </Tabs>
      </main>

      <AlertDialog open={!!suspending} onOpenChange={(v) => !v && setSuspending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Suspender este pedido?</AlertDialogTitle>
            <AlertDialogDescription>
              {suspending && <>Você vai suspender o pedido de <b>{suspending.quantidade} {suspending.unidade}</b> de <b>{suspending.item_nome}</b>. Essa ação não pode ser desfeita.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="suspend-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSuspend} data-testid="suspend-confirm" className="bg-red-600 hover:bg-red-700">
              Sim, suspender
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
